import ArrayProxy from "@ember/array/proxy"
import { action, computed, get } from "@ember/object"
import PromiseProxyMixin from "@ember/object/promise-proxy-mixin"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { CardStackSummary } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import config from "cc-frontend/config/environment"
import findMany from "cc-frontend/helpers/find-many"
import { prepareCourseSetDoNotApplyTemplatesAutomatically } from "cc-frontend/lib/actions/course/COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY"
import type { PromiseObject } from "cc-frontend/lib/promise-object"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import { compact, filter, map, uniq } from "lodash-es"
import styles from "./item.module.css"
const ArrayPromiseProxy = ArrayProxy.extend(PromiseProxyMixin)

interface Signature {
  Args: {
    course: Course
    usedTemplates: PromiseObject<Array<CardStackSummary>>
    courseTemplateIds: Array<string>
  }
}

export default class CourseTemplateItemComponent extends Component<Signature> {
  readonly styles = styles

  @service("ember-store") store!: EmberStore
  @service dispatcher!: Dispatcher

  blankTemplateId = config.BLANK_TEMPLATE_ID
  editMenuConstraints = [{ to: "window", pin: ["top", "bottom"] }]

  @findMany("courseTemplateIds", "card-stack-summary") courseTemplates!: PromiseObject<
    Array<CardStackSummary>
  >

  @computed("args.course.attributes.calendar.version")
  get courseTemplateIds() {
    return uniq(
      compact(
        map(
          filter(
            get(this.args.course.attributes.calendar, "dates"),
            (date) => get(date, "type") === "course-date-default"
          ),
          (date) => get(date.attributes, "cardStackId")
        )
      )
    )
  }

  get templateTitles() {
    return map(
      compact(get(get(this, "courseTemplates"), "content")),
      (cardStack: CardStackSummary) => {
        return cardStack?.attributes?.title
      }
    ).join(", ")
  }

  get noTemplates() {
    return this.courseTemplateIds.length === 0
  }

  get onlyOneTemplate() {
    return this.courseTemplateIds.length === 1
  }

  get onlyTemplateId() {
    return this.courseTemplateIds[0]
  }

  get hasMultipleTemplates() {
    return this.courseTemplateIds.length > 1
  }

  @computed("args.course.attributes.doNotApplyTemplatesAutomatically")
  get appliesTemplatesAutomatically() {
    return get(this.args.course.attributes, "doNotApplyTemplatesAutomatically") !== true
  }

  @action toggleApplyTemplatesAutomatically() {
    this.dispatcher.dispatchPreparedAction(
      prepareCourseSetDoNotApplyTemplatesAutomatically({
        courseId: this.args.course.id,
        planbookId: this.args.course.relationships.planbook!.data.id,
        doNotApplyTemplatesAutomatically: this.appliesTemplatesAutomatically,
      })
    )
  }

  @tracked menuIsOpen = false
  @action toggleMenu() {
    this.menuIsOpen = !this.menuIsOpen
  }
}
